import {
  createAsyncThunk,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { RootState } from '../../app/store';
import { LOADING_STATUS } from '../../common/constants';
import blockedPeriodsService from '../../common/api/blockedPeriodsService';
import { formatIsoDateToDateOnlyString } from '../../common/utils/dateUtils';

export interface BlockedPeriodListItem {
  id: string;
  startDate: string;
  endDate: string;
  typeDescription: string;
  isCopyable: string;
  type?: number;
  iCalExternalUid?: string;
  iCalImportId?: string;
  description?: string;
  iCalFullDescription?: string;
  iCalLocation?: string;
  iCalDistributionChannelName?: string;
  adults?: number;
  children?: number;
  ownerBookingNotes?: string;
  checkInTime?: string;
  checkOutTime?: string;
}

interface BlockedPeriodsState {
  blockedPeriodItems: BlockedPeriodListItem[];
  status: LOADING_STATUS.IDLE | LOADING_STATUS.LOADING | LOADING_STATUS.FAILED;
}

const initialState: BlockedPeriodsState = {
  blockedPeriodItems: [],
  status: LOADING_STATUS.IDLE,
};

export const getBlockedPeriods = createAsyncThunk(
  'blockedPeriods/getBlockedPeriods',
  async (accommodationId: string, { rejectWithValue }) => {
    try {
      const response =
        await blockedPeriodsService.getBlockedPeriodsByAccommodation(
          accommodationId,
        );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

export const deleteBlockedPeriod = createAsyncThunk(
  'blockedPeriods/deleteBlockedPeriod',
  async (blockedPeriodId: string, { rejectWithValue }) => {
    try {
      await blockedPeriodsService.deleteBlockedPeriod(blockedPeriodId);
      return blockedPeriodId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  },
);

const blockedPeriodsSlice = createSlice({
  name: 'blockedPeriods',
  initialState,
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(getBlockedPeriods.pending, state => {
        state.status = LOADING_STATUS.LOADING;
      })
      .addCase(getBlockedPeriods.fulfilled, (state, action) => {
        state.status = LOADING_STATUS.IDLE;
        state.blockedPeriodItems = action.payload;
      })
      .addCase(getBlockedPeriods.rejected, (state, action) => {
        state.status = LOADING_STATUS.FAILED;
      })
      .addCase(deleteBlockedPeriod.fulfilled, (state, action) => {
        state.blockedPeriodItems = state.blockedPeriodItems.filter(
          item => item.id !== action.payload,
        );
      });
  },
});

export default blockedPeriodsSlice.reducer;

export const selectBlockedPeriodItems = (state: RootState) =>
  state.blockedPeriods.blockedPeriodItems;

// Memoized selector that only recompute their value when their input selectors have changed.
export const selectBlockedPeriodDataTableItems = createSelector(
  selectBlockedPeriodItems,
  blockedPeriodItems =>
    blockedPeriodItems.map(item => ({
      ItemId: item.id,
      Items: [
        formatIsoDateToDateOnlyString(item.startDate),
        formatIsoDateToDateOnlyString(item.endDate),
        item.typeDescription,
        item.isCopyable ? '✔️' : '\u274C',
      ],
      Url: `/rates/blockedperiods/details/${item.id}`,
    })),
);

export const selectStatus = (state: RootState) => state.blockedPeriods.status;
